/* Lo que está en el tablero de herramientas y todavía no en Stock (Nico, 20/10).
 *
 * ── DOS CATÁLOGOS, Y CADA PANTALLA MIRA UNO ──────────────────────────────────
 *
 *   · `stock` (+ el JSON de `depositos.stock` + los eventos STOCK): el padrón
 *     de artículos que entra y sale de un depósito. Lo usan «Altas y bajas
 *     manuales», «Movimiento entre depósitos», «Artículo (stock)» y la página
 *     del pañol de cada sede en FlotaBot.
 *   · `taller_herramienta` (+ préstamos y entregas): el tablero. Lo que se da
 *     de alta con «Agregar herramienta» o por WhatsApp («dar de alta la
 *     herramienta …»), y lo que se entrega a la caja de cada uno.
 *
 * Nico dio de alta «Guante Tela» en el tablero de su oficina, fue a cargarle
 * stock a su depósito y el buscador le dijo «Sin opciones»: para Stock no
 * existía. Nada en la pantalla decía que había otro padrón.
 *
 * ── UN PUENTE, NO UN CATÁLOGO NUEVO ──────────────────────────────────────────
 *
 * Los dos se quedan como están —el tablero cuenta lo que se presta, Stock lo
 * que hay en cada depósito—. Lo que cambia es que el buscador de Stock, cuando
 * lo escrito está en el tablero y no en Stock, lo ofrece, y con un click se
 * crea el artículo con lo que el tablero ya sabe. Y si no está en ningún lado,
 * lo dice y ofrece crearlo ahí mismo en vez de «Sin opciones».
 *
 * ── CUÁNDO UNA HERRAMIENTA «YA TIENE» SU ARTÍCULO ────────────────────────────
 *
 * Si un artículo apunta a ella (`stock.herramienta_id`, que llega con la
 * migración `20261020210000`) o si se llama igual: mismas palabras sin
 * tildes, mayúsculas, signos, plurales ni las que no dicen nada («de», «para»,
 * …). Lo segundo es lo que vale mientras la columna no exista, y también cubre
 * lo que alguien dio de alta a mano con el mismo nombre. Ofrecer crear algo
 * que ya está fabricaría el duplicado que después nadie sabe cuál usar.
 *
 * Lo de los plurales y las palabras vacías vino de la revisión: Nico lo llama
 * «guantes de tela» y el tablero dice «Guante Tela». Comparando letra por
 * letra, la herramienta no aparecía y lo único que se ofrecía era crear otra.
 *
 * Es el gemelo de `panol_stock.herramientas_sin_articulo` y
 * `herramientas_para_ofrecer` de FlotaBot: la página del pañol de cada sede
 * ofrece lo mismo. Si tocás uno, tocá el otro; las tablas de casos de
 * `stockDesdeTablero.test.js` y de `test_panol_stock_tablero.py` son las
 * mismas.
 */

import { pelar } from "./herramientas";

/* «Guante  Tela», «GUANTE TELA» y «guante-tela» son el mismo nombre. */
export const normal = (t) =>
  pelar(t)
    .replace(/[^A-Z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/* Las que no dicen nada al comparar nombres. Si lo escrito es SÓLO eso
 * («con», que puede ser el principio de «conector»), se queda. */
export const PALABRAS_VACIAS = new Set([
  "DE", "DEL", "LA", "EL", "LOS", "LAS", "UN", "UNA", "PARA", "CON", "Y", "AL", "EN",
]);

/* «GUANTES» y «GUANTE» son lo mismo, y «DESTORNILLADORES» y «DESTORNILLADOR»
 * también: se le saca la S y después la E del final. No es la regla del
 * castellano —«LUCES» no vuelve a «LUZ»—, pero se aplica igual a los dos
 * lados, y lo que queda siempre es el principio de la palabra, así que sigue
 * sirviendo para buscar mientras se escribe. Los números no se tocan. */
export const raiz = (p) => {
  if (/^\d+$/.test(p)) return p;
  let r = p;
  if (r.length > 3 && r.endsWith("S")) r = r.slice(0, -1);
  if (r.length > 3 && r.endsWith("E")) r = r.slice(0, -1);
  return r;
};

/* Todas las palabras de un nombre, y las que cuentan: sin las vacías, salvo
 * que no haya otras. */
const palabras = (t) => normal(t).split(" ").filter(Boolean);
const llenas = (t) => {
  const todas = palabras(t);
  const sinVacias = todas.filter((p) => !PALABRAS_VACIAS.has(p));
  return sinVacias.length ? sinVacias : todas;
};

/* Las palabras que cuentan de un nombre, cada una en su raíz. */
export const claves = (t) => llenas(t).map(raiz);

/* El nombre reducido a lo que se compara: sus claves, sin repetir y en orden.
 * Dos nombres son el mismo si tienen la misma firma (y no está vacía). */
export const firma = (t) => [...new Set(claves(t))].sort().join(" ");

/* ¿Es el mismo nombre? Las mismas palabras, en cualquier orden. */
export const mismaDescripcion = (a, b) => {
  const x = firma(a);
  return x !== "" && x === firma(b);
};

/* El artículo de Stock que ya corresponde a esa herramienta, o `null`. */
export function articuloDe(herramienta, articulos = []) {
  if (!herramienta) return null;
  const id = String(herramienta.id ?? "");
  return (
    (articulos || []).find(
      (a) => a?.herramienta_id != null && String(a.herramienta_id) === id,
    ) ||
    (articulos || []).find(
      (a) =>
        mismaDescripcion(a?.descripcion, herramienta.nombre) ||
        mismaDescripcion(a?.descripcion, herramienta.codigo),
    ) ||
    null
  );
}

/* ── STOCK SE MIRA UNA VEZ, NO UNA VEZ POR HERRAMIENTA ────────────────────────
 *
 * Preguntarle a `articuloDe` por cada herramienta recorre el padrón entero
 * cada vez: con el de producción (1.842 artículos) y el tablero (35 activas)
 * son ~130.000 comparaciones y ~800 ms de CPU, y la lista de «Altas y bajas»
 * lo hacía en cada letra (verificación (2) del 20/10: escribir «guantes de
 * tela» costaba 14 s de CPU). Lo que hace falta saber de Stock —a qué
 * herramientas apunta y qué firmas tiene— se arma una vez y después cada
 * pregunta es una búsqueda en un Set. Da lo mismo que `articuloDe`: lo fija
 * `stockDesdeTablero.test.js`. */
export function indiceDeStock(articulos = []) {
  const vinculos = new Set();
  const firmas = new Set();
  (articulos || []).forEach((a) => {
    if (a?.herramienta_id != null) vinculos.add(String(a.herramienta_id));
    const f = firma(a?.descripcion);
    if (f) firmas.add(f);
  });
  return { vinculos, firmas };
}

const tieneArticulo = (h, { vinculos, firmas }) =>
  vinculos.has(String(h.id ?? "")) ||
  firmas.has(firma(h.nombre)) ||
  firmas.has(firma(h.codigo));

/* Las del tablero que Stock no tiene. Las dadas de baja no cuentan: ya no
 * están en ningún tablero. Quien ya tiene el índice de Stock lo pasa y no se
 * vuelve a armar. */
export const herramientasSinArticulo = (
  herramientas = [], articulos = [], indice = indiceDeStock(articulos),
) =>
  (herramientas || []).filter(
    (h) => h && h.activo !== false && !tieneArticulo(h, indice),
  );

/* El depósito de Stock de cada pañol del tablero. Explícito y no deducido del
 * nombre: el pañol del puerto se llama `puerto` y su depósito `terminal`. Los
 * cuatro de Torcuato usan el mismo id en los dos lados
 * (`20261007232837_torcuato_zona_comun`). La zona común (`bsas`) y Jow Kon no
 * tienen depósito propio, y no se les inventa uno. Es el mismo mapa que
 * `panol_padron.SEDE_DEPOSITOS` de FlotaBot, visto desde el tablero. */
export const DEPOSITO_DEL_PANOL = {
  tractores: "tractores",
  furgones: "furgones",
  "oficina-nico": "oficina-nico",
  fiscal: "fiscal",
  puerto: "terminal",
  "bahia-blanca": "bahia",
  "rio-gallegos": "gallegos",
  "rio-grande": "grande",
  ushuaia: "ushuaia",
};

/* ── LO ESCRITO CONTRA UN NOMBRE: PALABRA POR PALABRA ─────────────────────────
 *
 * ¿Todas las palabras de lo escrito están en el nombre o el código? Por
 * palabras y no como un pedazo de texto: «tela guante» también es el guante.
 * Cada palabra escrita (sin las vacías) cuenta si una palabra del nombre
 *
 *   · es la misma en su raíz: «guantes de tela» encuentra «Guante Tela»
 *     (revisión del 20/10: así lo llama Nico), o
 *   · empieza con lo que se escribió: «guan» la encuentra mientras se escribe.
 *
 * Antes se buscaba la raíz como pedazo adentro del nombre, y «guantes»
 * (raíz GUANT) traía también la GUANTERA de IVECO (verificación (2) del
 * 20/10). Ahora «guantes» no es el principio de «guantera» ni tiene su raíz.
 * «guante» sí es su principio, y la trae, igual que el filtro de siempre de la
 * lista: mientras se escribe no se sabe si va a terminar en «guantera».
 *
 * `pedidasDe` y `palabrasDe` dejan cada lado listo para comparar; quien
 * compara lo mismo contra muchos nombres (la lista de Stock en cada tecla) los
 * arma una vez y usa `estanTodas`. */
export const pedidasDe = (texto) => llenas(texto).map((p) => [p, raiz(p)]);

export const palabrasDe = (...textos) =>
  textos.flatMap(palabras).map((p) => [p, raiz(p)]);

export const estanTodas = (pedidas, delNombre) =>
  pedidas.length > 0 &&
  pedidas.every(([escrita, suRaiz]) =>
    delNombre.some(([p, r]) => r === suRaiz || p.startsWith(escrita)),
  );

export const nombra = (herramienta, texto) =>
  estanTodas(pedidasDe(texto), palabrasDe(herramienta?.nombre, herramienta?.codigo));

/* Con menos de dos letras no se ofrece nada: con una sola, la lista sería el
 * tablero entero. */
const muyCorto = (texto) => normal(texto).replace(/ /g, "").length < 2;

/* Lo que se ofrece mientras se escribe, de entre las que ya se sabe que no
 * tienen artículo: las que nombra el texto, primero las del pañol de ESE
 * depósito —son las que casi seguro se están cargando—. Es lo único que hace
 * falta en cada tecla, y recorre una lista corta. */
export function ofrecerEntre(sinArticulo, texto, { deposito = "", tope = 6 } = {}) {
  if (muyCorto(texto)) return [];
  const pedidas = pedidasDe(texto);
  const delDeposito = (h) =>
    deposito && DEPOSITO_DEL_PANOL[h.panol] === deposito ? 0 : 1;
  return (sinArticulo || [])
    .filter((h) => estanTodas(pedidas, palabrasDe(h.nombre, h.codigo)))
    .sort(
      (a, b) =>
        delDeposito(a) - delDeposito(b) ||
        String(a.nombre || "").localeCompare(String(b.nombre || ""), "es"),
    )
    .slice(0, tope);
}

/* Lo mismo desde el tablero y Stock enteros: para una sola pregunta (y para
 * la tabla de casos gemela de FlotaBot). Una pantalla que pregunta en cada
 * tecla calcula `herramientasSinArticulo` una vez y usa `ofrecerEntre`. */
export const herramientasParaOfrecer = (herramientas, articulos, texto, opciones = {}) =>
  muyCorto(texto)
    ? []
    : ofrecerEntre(herramientasSinArticulo(herramientas, articulos), texto, opciones);

/* LO QUE SE ENTREGA Y NO VUELVE SE CONSUME. En el tablero eso se escribe con
 * un plazo de préstamo enorme («Guante Tela»: 1.100.000 días, «No se
 * devuelve»); en Stock, los guantes son INSUMO (`IN56…`). Lo demás se presta y
 * vuelve: es una HERRAMIENTA. Es una sugerencia: el tipo se elige antes de
 * crear. */
export const DIAS_DE_LO_QUE_NO_VUELVE = 3650;

export const tipoSugerido = (herramienta) =>
  Number(herramienta?.dias_prestamo) >= DIAS_DE_LO_QUE_NO_VUELVE
    ? "INSUMO"
    : "HERRAMIENTA";

export const UNIDAD_POR_DEFECTO = "UNIDADES";

/* Lo que se precarga para crear el artículo. Desde una herramienta, con lo
 * que el tablero ya sabe; sin ella, con lo que se escribió. La descripción va
 * en mayúsculas, como el resto del padrón. */
export function borradorDeArticulo({ herramienta = null, texto = "", panolNombre = "" } = {}) {
  if (herramienta) {
    const donde = panolNombre || herramienta.panol || "";
    return {
      descripcion: String(herramienta.nombre || "").replace(/\s+/g, " ").trim().toUpperCase(),
      tipo: tipoSugerido(herramienta),
      unidad: UNIDAD_POR_DEFECTO,
      detalle: `Del tablero de herramientas: ${herramienta.codigo || herramienta.nombre}` +
        (donde ? ` (${donde})` : ""),
      herramienta,
    };
  }
  return {
    descripcion: String(texto || "").replace(/\s+/g, " ").trim().toUpperCase(),
    tipo: "INSUMO",
    unidad: UNIDAD_POR_DEFECTO,
    detalle: "",
    herramienta: null,
  };
}

/* El proveedor de lo que se da de alta desde acá. El código del artículo se
 * arma con el tipo y el proveedor (`generarCodigoStock`) y sin proveedor queda
 * VACÍO; el `01` es GENÉRICO, el mismo que usa FlotaBot
 * (`panol_stock.PROVEEDOR_GENERICO`). El proveedor real se completa después,
 * editando el artículo, si hace falta. */
export const PROVEEDOR_GENERICO = "01";

/* El código que sigue para ese prefijo: `IN01` + el correlativo de cuatro.
 * Gemelo de `panol_stock.proximo_codigo`. */
export function proximoCodigo(prefijo, ids = []) {
  const p = String(prefijo || "").toUpperCase();
  const ultimos = (ids || [])
    .map((x) => String(x || "").toUpperCase())
    .filter((c) => c.startsWith(p) && /^\d+$/.test(c.slice(p.length)))
    .map((c) => Number(c.slice(p.length)));
  const sigue = (ultimos.length ? Math.max(...ultimos) : 0) + 1;
  return `${p}${String(sigue).padStart(4, "0")}`;
}

/* La fila de `stock` que se inserta. `cantidad` arranca en cero: la suma la
 * hace el movimiento, que es lo único que toca saldos. */
export function filaDeArticulo(codigo, borrador, { conVinculo = true } = {}) {
  const fila = {
    codigo,
    descripcion: String(borrador?.descripcion || "").replace(/\s+/g, " ").trim().toUpperCase(),
    tipo: borrador?.tipo || "INSUMO",
    unidad: borrador?.unidad || UNIDAD_POR_DEFECTO,
    proveedor: PROVEEDOR_GENERICO,
    cantidad: "0",
    detalle: borrador?.detalle || null,
  };
  if (conVinculo && borrador?.herramienta?.id != null) {
    fila.herramienta_id = borrador.herramienta.id;
  }
  return fila;
}

/* Cómo se rotula un artículo en la lista: la descripción y el proveedor,
 * igual que los que arma `cargarSelects` (`dataFunctions`). El que se acaba
 * de crear entra con el mismo rótulo, y ése es también el que queda escrito
 * en el renglón del movimiento. */
export const rotuloDeArticulo = (a) =>
  [a?.descripcion, a?.nombreProveedor].filter(Boolean).join(" ");

/* ¿Qué le falta al borrador para poder crearse? `""` si nada. */
export function faltaEnBorrador(borrador, tipos = []) {
  if (normal(borrador?.descripcion).length < 3) return "Escribí qué es (tres letras o más).";
  if (tipos.length && !tipos.includes(borrador?.tipo)) return "Elegí el tipo.";
  if (!String(borrador?.unidad || "").trim()) return "Elegí la unidad.";
  return "";
}
